import { errorInfo, railTraceDebug } from '@/lib/railTrace/debugLog';

import { recordPointcloudFetchFailure } from './debugLog';

/**
 * While held, the point-cloud fetch wrapper (`Pointcloud.tsx`) sends Range
 * requests with `cache: 'no-store'`.
 *
 * Why: octree node data is byte ranges of ONE file (octree.bin /
 * hierarchy.bin). Chrome's HTTP cache takes a lock on a URL while a response
 * for it is being written to the cache, so concurrent range requests on the
 * same URL are serialised: one node per round trip. For a burst of
 * full-resolution reads (the rail follower: measured 2026-10-27 with S3-like
 * headers, 73 s per 100 m of a dense scan instead of 2 s) that lock is the
 * whole cost; `no-store` skips the cache and the loads run in parallel over
 * HTTP/2.
 *
 * Why not always: for normal viewing the cache DOES help — Chrome reuses
 * sparse 206 entries, so panning back to an area is served from disk instead
 * of the CDN. So the bypass is held only for the duration of a burst that
 * needs it (a rail trace), ref-counted so overlapping holders compose.
 */
let holders = 0;

/** Bypass the HTTP cache for ranged point-cloud fetches until the returned release is called (once). */
export function acquireRangeCacheBypass(): () => void {
  holders++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders--;
  };
}

/** Is any holder bypassing the cache right now? */
export function rangeCacheBypassed(): boolean {
  return holders > 0;
}

/** The `RequestInit` overrides for a point-cloud fetch: `no-store` for a Range request while bypassed. */
export function rangeCacheInit(init?: RequestInit): { cache?: RequestCache } {
  if (holders === 0) return {};
  return hasRange(init?.headers) ? { cache: 'no-store' } : {};
}

/** Does a `HeadersInit` carry a Range header (any case, any of its three shapes)? */
function hasRange(h: HeadersInit | undefined): boolean {
  if (!h) return false;
  if (typeof (h as Headers).has === 'function') return (h as Headers).has('Range');
  const names = Array.isArray(h) ? h.map(([k]) => k) : Object.keys(h);
  return names.some((k) => k.toLowerCase() === 'range');
}

/** Pause after the first failure of a request (ms); doubled on each further one, up to `FETCH_BACKOFF_MAX_MS`. */
export const FETCH_BACKOFF_BASE_MS = 1000;
export const FETCH_BACKOFF_MAX_MS = 30_000;
/**
 * A 403 / 404 is not going away by itself (an expired signed cookie, a file
 * that is not there): the request is not sent again for this long, unless
 * `resetPointCloudFetchFailures` (new credentials) or a page reload clears it.
 */
export const FETCH_PERSISTENT_COOLDOWN_MS = 5 * 60_000;
/** How long a `grantPointCloudFetchRetry` pass waits to be used (ms). */
const RETRY_PASS_TTL_MS = 60_000;
/** Records kept at most (oldest dropped): a whole cloud failing must not grow this without bound. */
const MAX_FAILURE_RECORDS = 20_000;

type FailureRecord = { failures: number; until: number; status?: number };
/** Per request (file + byte range): consecutive failures and when it may be sent again. */
const failures = new Map<string, FailureRecord>();
/** Byte ranges a reader with its own bounded retries (`walkPointCloud`) may send once despite a backoff. */
const retryPasses = new Map<string, number>();

/**
 * Thrown without touching the network while a request is backing off: the
 * same request failed a moment ago (or for good: 403 / 404).
 */
export class PointCloudFetchBackoffError extends Error {
  readonly status: number | undefined;
  readonly retryInMs: number;
  constructor(status: number | undefined, retryInMs: number) {
    super(`Point cloud request backing off${status ? ` after HTTP ${status}` : ''} (${Math.ceil(retryInMs / 1000)} s)`);
    this.name = 'PointCloudFetchBackoffError';
    this.status = status;
    this.retryInMs = retryInMs;
  }
}

/**
 * Forget every failure (new signed cookies were set, the user asked to retry):
 * the next request of each goes out again.
 */
export function resetPointCloudFetchFailures() {
  failures.clear();
  retryPasses.clear();
}

/**
 * Let the next request of `node`'s byte ranges (its node data and, for a
 * hierarchy proxy, its hierarchy chunk) go out even while it backs off. For a
 * reader with retries of its own (the rail follower's `walkPointCloud`), whose
 * every attempt must reach the network; its attempts are bounded, so this
 * cannot loop. A request made with a pass still records its failure.
 */
export function grantPointCloudFetchRetry(node: {
  byteOffset?: bigint | number;
  byteSize?: bigint | number;
  hierarchyByteOffset?: bigint | number;
  hierarchyByteSize?: bigint | number;
}) {
  const until = performance.now() + RETRY_PASS_TTL_MS;
  const add = (o?: bigint | number, n?: bigint | number) => {
    if (o === undefined || n === undefined) return;
    const first = BigInt(o);
    const size = BigInt(n);
    if (size <= BigInt(0)) return;
    retryPasses.set(`bytes=${first}-${first + size - BigInt(1)}`, until);
  };
  add(node.byteOffset, node.byteSize);
  add(node.hierarchyByteOffset, node.hierarchyByteSize);
  if (retryPasses.size > MAX_FAILURE_RECORDS) {
    const now = performance.now();
    for (const [k, t] of retryPasses) if (t < now) retryPasses.delete(k);
  }
}

/** Use up a pass for `range`, if one is there and fresh. */
function takeRetryPass(range: string | undefined): boolean {
  if (!range) return false;
  const t = retryPasses.get(range);
  if (t === undefined) return false;
  retryPasses.delete(range);
  return t >= performance.now();
}

function recordFailure(key: string, status: number | undefined) {
  const prev = failures.get(key);
  const n = (prev?.failures ?? 0) + 1;
  const persistent = status === 403 || status === 404;
  const wait = persistent
    ? FETCH_PERSISTENT_COOLDOWN_MS
    : Math.min(FETCH_BACKOFF_MAX_MS, FETCH_BACKOFF_BASE_MS * 2 ** Math.min(n - 1, 16));
  failures.delete(key);
  failures.set(key, { failures: n, until: performance.now() + wait, status });
  if (failures.size > MAX_FAILURE_RECORDS) failures.delete(failures.keys().next().value!);
}

/**
 * A point-cloud fetch, checked:
 *
 * - An HTTP error (a 403 from an expired signed cookie, a 5xx / 503 SlowDown
 *   from the CDN) is thrown instead of handed to the loader. The loader does
 *   not look at the status, so it would decode the error body as node data:
 *   the worker then fails without a reply and the node stays "loading"
 *   forever. Thrown, the loader marks the load failed and the caller can try
 *   again (`walkPointCloud` retries).
 * - A failed request backs off. The loader keeps no memory of a failure (it
 *   resets loaded / loading), so Potree's render update asks for every visible
 *   failed node again on the next frame: without this, an expired cookie or a
 *   throttling CDN became a request loop at frame rate. A request that failed
 *   is not sent again (`PointCloudFetchBackoffError`, no network) for 1 s,
 *   then 2, 4, … up to 30 s; a 403 / 404 for `FETCH_PERSISTENT_COOLDOWN_MS`
 *   (or until `resetPointCloudFetchFailures`). A success clears it. A reader
 *   with bounded retries of its own sends its attempts anyway
 *   (`grantPointCloudFetchRetry`).
 *
 * Failures (not the backed-off calls) are recorded in the rail trace debug log when it is on.
 */
export async function checkedPointCloudFetch(
  doFetch: () => Promise<Response>,
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const range = rangeOf(init?.headers);
  const url = shortUrl(input);
  const key = `${fullPath(input)}|${range ?? ''}`;
  const rec = failures.get(key);
  if (rec) {
    const wait = rec.until - performance.now();
    if (wait > 0 && !takeRetryPass(range)) throw new PointCloudFetchBackoffError(rec.status, wait);
  }
  let res: Response;
  try {
    res = await doFetch();
  } catch (e) {
    if ((e as Error)?.name !== 'AbortError') recordFailure(key, undefined);
    railTraceDebug('fetchFail', { url, range, bypass: holders > 0, error: errorInfo(e) });
    recordPointcloudFetchFailure({ url, range, error: String((e as Error)?.message ?? e) });
    throw e;
  }
  if (!res.ok) {
    recordFailure(key, res.status);
    railTraceDebug('fetchFail', { url, range, bypass: holders > 0, status: res.status });
    recordPointcloudFetchFailure({ url, range, status: res.status });
    throw new Error(`Point cloud request failed: HTTP ${res.status}${range ? ` (${range})` : ''}`);
  }
  if (rec) failures.delete(key);
  return res;
}

function rangeOf(h: HeadersInit | undefined): string | undefined {
  if (!h) return undefined;
  if (typeof (h as Headers).get === 'function') return (h as Headers).get('Range') ?? undefined;
  const entries = Array.isArray(h) ? h : Object.entries(h);
  return entries.find(([k]) => k.toLowerCase() === 'range')?.[1];
}

/** A URL without its query (signed URLs carry credentials, which change on a refresh). */
const fullPath = (input: RequestInfo | URL) => {
  const s = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  return s.split('?')[0];
};
/** The file part of a URL (no query: signed URLs carry credentials). */
const shortUrl = (input: RequestInfo | URL) => fullPath(input).split('/').slice(-2).join('/');
